import { useMemo, useState } from 'react';
import { StepperField } from '@oberoninternal/travelbase-ds/components/form/Stepper';
import Portal from '@reach/portal';
import { Box, Flex } from '@rebass/grid';
import { Formik } from 'formik';
import ErrorMessage from '@oberoninternal/travelbase-ds/components/form/ErrorMessage';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime, useIntl } from 'react-intl';
import { gql } from '@apollo/client';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import messages from '../constants/messages';
import parseDateTime from '../utils/parseDateTime';
import BookButton from './designsystem/BookButton';
import Stack from './Stack';
import { ModalProps } from '@oberoninternal/travelbase-ds/components/layout/Modal';
import { Activity, ActivityCheckoutModalTimeslotFragment, TicketCartItemInput } from '../generated/graphql';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import styled from 'styled-components';
import StickyBar from './StickyBar';
import EditIcon from './svg/Edit';

export const fragment = gql`
    fragment ActivityCheckoutModalTimeslot on Timeslot {
        id
        startDateTime
        endDateTime
        allotment
        rateGroup {
            rates {
                id
                name
                price
            }
        }
    }
`;

export interface ActivityTicketsProps extends ModalProps {
    activity?: Pick<Activity, 'name'>;
    timeslot?: ActivityCheckoutModalTimeslotFragment | null;
    setTicket?: (ticket: TicketCartItemInput | null) => Promise<void> | void;
    ticket?: TicketCartItemInput | null;
    setEdit?: () => void;
    setStickBar?: boolean;
}

const computeTotalPersons = (values: Record<string, { amount: number }>) =>
    Object.values(values).reduce((total, next) => total + next.amount, 0);

const ActivityTickets = ({ onClose, timeslot, ticket, setTicket, setEdit, setStickBar }: ActivityTicketsProps) => {
    const initialValues: Record<string, { amount: number }> = useMemo(
        () =>
            Object.assign(
                {},
                ...(timeslot?.rateGroup?.rates.map(rate => ({
                    [rate.id]: { amount: ticket?.rateLines.find(rateLine => rateLine.rateId === rate.id)?.amount ?? 0 },
                })) ?? [])
            ),
        [ticket, timeslot]
    );

    const intl = useIntl();
    const [removing, setRemoving] = useState(false);

    const onRemove = async (disableIndicator = false) => {
        if (
            !timeslot ||
            !window.confirm(
                intl.formatMessage(
                    {
                        defaultMessage: 'Weet je zeker dat je de {type} uit je mandje wilt verwijderen?',
                    },
                    { type: intl.formatMessage({ defaultMessage: 'activiteit' }) }
                )
            )
        ) {
            return;
        }
        if (!disableIndicator) {
            setRemoving(true);
        }
        await setTicket?.(null);

        if (!disableIndicator) {
            setRemoving(false);
        }

        onClose();
    };

    return (
        <TicketsWrapper>
            <Box width={['unset', null, '30%']}>
                <EditWrapper>
                    <EditLabelWrapper>
                        <EditLabel>
                            <FormattedDate
                                value={parseDateTime(timeslot?.startDateTime ?? '')}
                                format="WEEKDAYMONTHYEAR"
                            />
                        </EditLabel>
                        <EditLabel>
                            <FormattedTime value={parseDateTime(timeslot?.startDateTime ?? '')} /> —{' '}
                            <FormattedTime value={parseDateTime(timeslot?.endDateTime ?? '')} />
                        </EditLabel>
                    </EditLabelWrapper>
                    <EditIconWrapper>
                        <button type="button" onClick={setEdit}>
                            <EditIcon />
                        </button>
                    </EditIconWrapper>
                </EditWrapper>
                <MobileWrapper>
                    <MobileEditLabelWrapper>
                        <ChooseTicketsTitle>
                            <FormattedMessage defaultMessage="Koop Tickets" />
                        </ChooseTicketsTitle>
                        <EditLabel>
                            <FormattedDate
                                value={parseDateTime(timeslot?.startDateTime ?? '')}
                                format="WEEKDAYMONTHYEAR"
                            />
                        </EditLabel>
                        <EditLabel>
                            <FormattedTime value={parseDateTime(timeslot?.startDateTime ?? '')} /> —{' '}
                            <FormattedTime value={parseDateTime(timeslot?.endDateTime ?? '')} />
                        </EditLabel>
                    </MobileEditLabelWrapper>
                </MobileWrapper>
            </Box>
            <Box width={['unset', null, '70%']} px={[0, null, 4]}>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={async values => {
                        if (!timeslot) {
                            return;
                        }
                        const persons = computeTotalPersons(values);

                        if (!persons) {
                            await onRemove(true);
                            return;
                        }

                        await setTicket?.({
                            timeslotId: timeslot.id,
                            rateLines: Object.entries(values)
                                .map(([key, value]) => ({
                                    rateId: key,
                                    amount: value.amount,
                                }))
                                .filter(rateLine => !!rateLine.amount),
                        });
                        onClose();
                    }}
                >
                    {({ values, dirty, isSubmitting, submitForm }) => {
                        if (!Object.keys(values).length) {
                            return null;
                        }
                        const persons = computeTotalPersons(values);

                        const ticketBarContent = (
                            <>
                                {ticket && (
                                    <Box mr={3}>
                                        <Button
                                            disabled={isSubmitting}
                                            submitting={removing}
                                            onClick={() => onRemove()}
                                            variant="danger"
                                        >
                                            Verwijderen
                                        </Button>
                                    </Box>
                                )}
                                {typeof timeslot?.allotment === 'number' && persons >= timeslot.allotment && (
                                    <Box mr={3}>
                                        <ErrorMessage>
                                            {intl.formatMessage(
                                                {
                                                    defaultMessage:
                                                        'Er {allotment, plural, one {is slechts #} other {zijn slechts #} } beschikbaar',
                                                },
                                                { allotment: timeslot.allotment }
                                            )}
                                        </ErrorMessage>
                                    </Box>
                                )}
                                {!ticket && (
                                    <ButtonGroup>
                                        <StyledBookButton
                                            onClick={submitForm}
                                            disabled={!dirty || removing}
                                            type="submit"
                                            submitting={isSubmitting}
                                        >
                                            {intl.formatMessage(messages.buyTickets)}
                                        </StyledBookButton>
                                    </ButtonGroup>
                                )}
                                {ticket && (
                                    <ButtonGroup>
                                        <StyledBookButton
                                            onClick={() => {
                                                if (dirty) {
                                                    submitForm();
                                                } else {
                                                    onClose();
                                                }
                                            }}
                                            type="submit"
                                            submitting={isSubmitting}
                                        >
                                            <FormattedMessage defaultMessage="Aanpassen" />
                                        </StyledBookButton>
                                    </ButtonGroup>
                                )}
                            </>
                        );
                        return (
                            <>
                                {timeslot && (
                                    <Stack spacing={3}>
                                        {timeslot.rateGroup?.rates.map(rate => {
                                            const total = rate.price * values[rate.id].amount;
                                            return (
                                                <ActivityItems key={rate.id} alignItems="center">
                                                    <Flex flex={2} flexDirection={['column', null, 'row']}>
                                                        <Box flex={1}>
                                                            <TicketBody>{rate.name}</TicketBody>
                                                        </Box>
                                                        <Box flex={1}>
                                                            <Body>
                                                                {!rate.price && (
                                                                    <FormattedMessage defaultMessage="gratis" />
                                                                )}
                                                                {!!rate.price && (
                                                                    <FormattedNumber value={rate.price} format="EUR" />
                                                                )}
                                                            </Body>
                                                        </Box>
                                                    </Flex>
                                                    <Box flex={2}>
                                                        <StepperField
                                                            editable
                                                            variant="primary"
                                                            name={`['${rate.id}'].amount`}
                                                            maximum={
                                                                (timeslot.allotment ?? 100) -
                                                                persons +
                                                                values[rate.id].amount
                                                            }
                                                        />
                                                    </Box>
                                                    <Flex flex={1} justifyContent="flex-end">
                                                        <Body>
                                                            <FormattedNumber value={total} format="EUR" />
                                                        </Body>
                                                    </Flex>
                                                </ActivityItems>
                                            );
                                        })}
                                        <TotalWrapper justifyContent="space-between">
                                            <Box>
                                                <TotalLabel variant="tiny" style={{ fontWeight: 300 }}>
                                                    <FormattedMessage defaultMessage="totaal" />
                                                </TotalLabel>
                                            </Box>
                                            <Box>
                                                <TicketPrice>
                                                    <FormattedNumber
                                                        value={
                                                            timeslot.rateGroup?.rates.reduce(
                                                                (acc, next) =>
                                                                    acc + next.price * values[next.id].amount,
                                                                0
                                                            ) ?? 0
                                                        }
                                                        format="EUR"
                                                    />
                                                </TicketPrice>
                                            </Box>
                                        </TotalWrapper>
                                    </Stack>
                                )}
                                <StyledTicketBar className="gt-s">{ticketBarContent}</StyledTicketBar>

                                {setStickBar && (
                                    <Portal>
                                        <TicketBarMobile className="lt-s">{ticketBarContent}</TicketBarMobile>
                                    </Portal>
                                )}
                            </>
                        );
                    }}
                </Formik>
            </Box>
        </TicketsWrapper>
    );
};

export default ActivityTickets;

const ButtonGroup = styled.div`
    > button + button {
        margin-left: ${({ theme }) => theme.spacing['40_Standard']};
    }
    > button {
        flex-shrink: 0;
    }
    display: flex;
    margin-left: auto;

    @media screen and (max-width: 720px) {
        width: 100%;
    }
`;

const TicketBarMobile = styled(StickyBar).attrs({ wrapInPortal: true })`
    display: flex;
    width: 100%;
    align-items: center;
    > div > div {
        height: unset;
    }
`;

const TicketBar = styled.div`
    align-items: center;
    padding-top: 2.4rem;
    margin-top: auto;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        margin-top: ${({ theme }) => theme.spacing['60_Large']};
    }
    border-top: 1px solid ${({ theme }) => theme.colors.neutral[20]};
    display: flex;
`;

const TicketBody = styled(Body)`
    font-weight: 400;
`;

const TicketPrice = styled(TicketBody)`
    font-weight: 600;
    font-size: 2rem;
    line-height: 2.4rem;
`;

const TicketsWrapper = styled.div`
    display: flex;

    @media screen and (max-width: 1024px) {
        display: block;
    }
`;

const EditWrapper = styled.div`
    display: flex;
    border: 1px solid ${({ theme }) => theme.colors.neutral[10]};
    border-radius: 8px;
    padding: 1.6rem;

    @media screen and (max-width: 720px) {
        display: none;
    }
`;

const EditLabelWrapper = styled.div`
    width: 50%;
`;

const MobileWrapper = styled(EditLabelWrapper)`
    display: none;

    @media screen and (max-width: 720px) {
        display: block;
        width: 100%;
        padding-top: 2rem;
        justify-content: space-between;
    }
`;

const MobileEditLabelWrapper = styled.div`
    display: block;
`;

const EditIconWrapper = styled.div`
    width: 50%;
    display: flex;
    justify-content: flex-end;

    @media screen and (max-width: 720px) {
        display: none;
    }
`;

const EditLabel = styled(Body)`
    font-weight: 400;
    font-size: 1.6rem;
    line-height: 2.4rem;
`;

const StyledTicketBar = styled(TicketBar)`
    border-top: none;
`;

const TotalWrapper = styled(Flex)`
    margin-top: 2.4rem !important;
`;

const TotalLabel = styled(Body)`
    text-transform: capitalize;
    font-weight: 600 !important;
    font-size: 2rem;
    line-height: 2.4rem;
`;

const ActivityItems = styled(Flex)`
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[10]};
    padding-bottom: 1.6rem;
    display: flex;
    justify-content: space-between;
`;

const StyledBookButton = styled(BookButton)`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.2rem 3.2rem;
    background-color: ${({ theme }) => theme.colors.primary[80]};
    border-radius: 99px;
    color: ${({ theme }) => theme.colors.primary[0]};
    font-weight: 600;
    font-size: 1.4rem;
    line-height: 2.4rem;
`;

const ChooseTicketsTitle = styled(Body)`
    font-weight: 700;
    font-size: 2.4rem;
    line-height: 2.4rem;
    margin-bottom: 1.6rem;
`;
